import { create } from 'zustand';
import signupsService from '../api/services/signups.service';
import { fmtTime } from '../utils/formatters';
import { signupPageLinkUrl } from '../utils/portalLinks';

let slotDateSeq = 1;
let slotWinSeq = 1;
let signupItemSeq = 1;

/* Client-side keys for rows the form just added — real dates/windows come back
   from Mongo with their own _ids, so these only ever live in the editor. */
export const nextSlotDateId = () => `newd${slotDateSeq++}`;
export const nextSlotWinId = () => `neww${slotWinSeq++}`;
export const nextSignupItemId = () => `newi${signupItemSeq++}`;

/* roomparent.com/signup/<shortCode>, built from the code the backend minted
   when the sign-up was created (not its stored publicURL, which older rows
   carry as an API /s/ short link). Rows saved before short codes existed have
   none — callers disable Copy link rather than hand out a URL that goes nowhere. */
export const signupLinkUrl = (s) => signupPageLinkUrl(s?.shortCode);

/* A sign-up is one kind: 'slots' (dates with time windows), 'open' ("By date" —
   dates with a capacity, no times) or 'items' (supplies parents claim). */
export const SIGNUP_KINDS = ['slots', 'open', 'items'];

/* A window's capacity is a whole number, or null when the Spots field was left
   blank — no limit on how many can sign up. */
export const isUnlimited = (w) => w.capacity == null;

/* One counter for all three kinds, so every list, chip and summary says the
   same thing without each caller re-deriving it. `cap` is null when any window
   has no limit, since a total over "unlimited" means nothing. */
export function signupCounts(s) {
  if (s.kind === 'items') {
    const items = s.items || [];
    return {
      cap: items.reduce((a, i) => a + i.qty, 0),
      filled: items.reduce((a, i) => a + i.claimed.length, 0),
      who: 'claimed',
    };
  }
  const windows = (s.dates || []).flatMap((d) => d.windows);
  return {
    cap: windows.some(isUnlimited) ? null : windows.reduce((a, w) => a + w.capacity, 0),
    filled: windows.reduce((a, w) => a + w.signed.length, 0),
    who: 'volunteers',
  };
}

/* How full a sign-up is, counted per window (per item for a supply list):
   people signed up, spots still open and windows with none left. A no-limit
   window adds to `filled` but can never be open-counted or full, so `unlimited`
   says how many of those there are. */
export function signupFill(s) {
  const rows =
    s.kind === 'items'
      ? (s.items || []).map((i) => ({ capacity: i.qty, signed: i.claimed }))
      : (s.dates || []).flatMap((d) => d.windows);
  const limited = rows.filter((w) => !isUnlimited(w));
  return {
    rows: rows.length,
    filled: rows.reduce((a, w) => a + w.signed.length, 0),
    open: limited.reduce((a, w) => a + Math.max(0, w.capacity - w.signed.length), 0),
    full: limited.filter((w) => w.signed.length >= w.capacity).length,
    unlimited: rows.length - limited.length,
  };
}

export const isWindowFull = (w) => !isUnlimited(w) && w.signed.length >= w.capacity;

/* "9 spots" / "unlimited spots", for a signupCounts() cap */
export const fmtSpotsTotal = (cap) => (cap == null ? 'unlimited spots' : `${cap} spot${cap === 1 ? '' : 's'}`);

/* "3/8 volunteers", or "3 volunteers" when there's no limit */
export const fmtFilled = ({ cap, filled, who }) => (cap == null ? `${filled} ${who}` : `${filled}/${cap} ${who}`);

/* "3 dates" / "4 items" */
export function signupUnitLabel(s) {
  const n = s.kind === 'items' ? (s.items || []).length : (s.dates || []).length;
  const unit = s.kind === 'items' ? 'item' : 'date';
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/* One family per Friday is not "1 spot left" and not "Full". A rota is taken
   or it is open; only a multi-spot shift counts down; no limit never fills. */
export function spotsLabel(w) {
  if (isUnlimited(w)) return 'Unlimited';
  const left = w.capacity - (w.signed || []).length;
  if (w.capacity === 1) return left > 0 ? 'Open' : 'Taken';
  return left > 0 ? `${left} spot${left === 1 ? '' : 's'} left` : 'Full';
}

/* What sits under a date: its label if it has one, else the time range, else
   nothing — a By-date row with no label already says everything it knows. */
export function slotSubLabel(d, w) {
  if (d?.label) return d.label;
  return w.anyTime || !w.start ? '' : `${fmtTime(w.start)}–${fmtTime(w.end)}`;
}

const useSignupsStore = create((set, get) => ({
  signups: [],
  signupsLoading: false,
  signupsTotal: 0,
  signupsTotalPages: 1,

  /* Server-paginated sign-ups for the selected network. `scope` is stamped on
     each row so the pages that filter by it (Links, Compose Email) keep
     working. */
  loadSignups: async (networkId, { page = 1, limit = 10 } = {}, scope = 'class') => {
    if (!networkId) return;
    set({ signupsLoading: true });
    try {
      const r = await signupsService.list(networkId, { page, limit });
      set({
        signups: r.signups.map((s) => ({ ...s, scope })),
        signupsTotal: r.total,
        signupsTotalPages: r.totalPages,
      });
    } finally {
      set({ signupsLoading: false });
    }
  },

  signupById: (id) => get().signups.find((s) => s.id === id) || null,

  /* Local mirrors of a write that already succeeded — the row updates without
     waiting on a refetch. */
  patchSignup: (id, patch) =>
    set({ signups: get().signups.map((s) => (s.id === id ? { ...s, ...patch } : s)) }),

  removeSignup: (id) =>
    set({
      signups: get().signups.filter((s) => s.id !== id),
      signupsTotal: Math.max(0, get().signupsTotal - 1),
    }),

  setIncludeInEmail: (id, includeInEmail) =>
    set({ signups: get().signups.map((s) => (s.id === id ? { ...s, includeInEmail } : s)) }),
}));

export default useSignupsStore;
